import { ReactNode, ButtonHTMLAttributes } from "react";

export type ButtonVariant = "primary" | "secondary" | "icon" | "toggle";
export type ButtonSize = "sm" | "md" | "lg";

type ButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children?: ReactNode;
  /** Optional icon to display before children */
  icon?: ReactNode;
  /** Full width button */
  fullWidth?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>;

/**
 * Reusable button component with predefined variants.
 *
 * Variants:
 * - primary: Main CTAs (submit forms, primary actions)
 * - secondary: Less prominent actions (scramble, generate patterns)
 * - icon: Square buttons with icons only (playback controls)
 * - toggle: Small inline toggle buttons (scramble toggles)
 *
 * Reduces repetition from 226-char class strings to ~25-char component calls.
 */
export default function Button({
  variant = "secondary",
  size = "md",
  children,
  icon,
  fullWidth,
  className,
  ...others
}: ButtonProps) {
  // Base classes shared by all variants
  const baseClasses = "flex items-center justify-center gap-2 rounded transition-colors border-2 font-medium focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed";

  const getVariantClass = () => {
    switch (variant) {
      case "primary":
        return "py-3 px-5 border-purple-700 hover:border-purple-500 focus:border-purple-400 bg-purple-700 hover:bg-purple-600 focus:ring-4 focus:ring-purple-800 text-white text-sm";
      case "secondary":
        return "border-gray-700 hover:border-purple-500 focus:border-purple-400 bg-gray-900/50 text-gray-300";
      case "icon":
        return "p-0 w-12 h-12 border-gray-700 hover:border-purple-500 focus:border-purple-400 active:border-green-500";
      case "toggle":
        return "border border-purple-700 hover:border-purple-500 focus:border-purple-400 w-6 h-6 align-middle mx-1";
      default:
        return "border-gray-700 hover:border-purple-500 bg-gray-900/50 hover:bg-purple-900/20 text-gray-300 hover:text-purple-300";
    }
  };

  const getSizeClass = () => {
    // Icon and toggle buttons have fixed sizes
    if (variant === "icon" || variant === "toggle") {
      return "";
    }

    // Size variants for primary/secondary buttons
    switch (size) {
      case "sm":
        return "py-2 px-3 text-sm";
      case "md":
        return "py-3 px-4 text-base";
      case "lg":
        return "py-4 px-6 text-lg";
      default:
        return "py-3 px-4 text-base";
    }
  };

  const widthClass = fullWidth ? "w-full" : "";

  const combinedClass = `${baseClasses} ${getVariantClass()} ${getSizeClass()} ${widthClass} ${className || ""}`.trim();

  // For icon-only buttons, let the button's aria-label provide the accessible name
  const renderContent = () => {
    return (
      <>
        {icon}
        {children}
      </>
    );
  };

  return (
    <button
      className={combinedClass}
      type="button"
      {...others}
    >
      {renderContent()}
    </button>
  );
}
